import React, { useState } from 'react';
import { generateVersionHistory, flattenServerVersionHistory } from '../utils/versionHistory';
import { lightenColor } from '../utils/colorUtils';

const VersionHistoryCompact = ({ client }) => {
  const [versionHistory, setVersionHistory] = useState(() => generateVersionHistory(client));
  const [expandedServer, setExpandedServer] = useState(null);
  const [expandedHistory, setExpandedHistory] = useState(false);
  const [expandedComponents, setExpandedComponents] = useState({});
  
  const toggleServerHistory = (serverIndex) => {
    if (expandedServer === serverIndex) {
      setExpandedServer(null);
    } else {
      setExpandedServer(serverIndex);
    }
  };
  
  const toggleHistoryVisibility = () => {
    setExpandedHistory(!expandedHistory);
  };
  
  // Fonction pour basculer l'affichage d'un composant spécifique
  const toggleComponent = (serverIndex, componentName) => {
    const key = `${serverIndex}-${componentName}`;
    setExpandedComponents(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };
  
  // Vérifier si un composant est développé
  const isComponentExpanded = (serverIndex, componentName) => {
    const key = `${serverIndex}-${componentName}`;
    return expandedComponents[key] || false;
  };
  
  // Fonction pour grouper les versions par composant
  const groupVersionsByComponent = (serverHistory) => {
    if (!serverHistory || !serverHistory.components) return [];
    
    return serverHistory.components
      .filter(comp => comp.versions && comp.versions.length > 0)
      .sort((a, b) => a.name.localeCompare(b.name));
  };
  
  // Fonction pour formater la date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };
  
  // Fonction pour déterminer la classe du badge
  const getBadgeClass = (updateType) => {
    switch(updateType) {
      case 'Production':
        return 'update-badge update-badge-prod';
      case 'Qualification':
        return 'update-badge update-badge-qual';
      case 'Initial':
        return 'update-badge bg-gray-100 text-gray-800';
      default:
        return 'update-badge bg-blue-100 text-blue-800';
    }
  };
  
  if (!client || !versionHistory || versionHistory.length === 0) {
    return null;
  }
  
  return (
    <div className="version-history mt-8 bg-white rounded-lg shadow-md overflow-hidden">
      <div 
        className="version-history-header p-4 flex items-center cursor-pointer border-b hover:bg-gray-50"
        onClick={toggleHistoryVisibility}
      >
        <div className="version-history-icon mr-2">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 3v18h18"></path>
            <path d="M18.4 9a9 9 0 0 0-9-9 9 9 0 0 0 0 18"></path>
            <path d="M16 8l-4.7.65a1 1 0 0 0-.85.9l-.29 2.8a1 1 0 0 0 .2.75l2.35 3.1a1 1 0 0 0 1.55.05l2-2.2a1 1 0 0 0 .25-.85l-.7-5.2z"></path>
          </svg>
        </div>
        <span className="text-lg font-semibold">Historique des modifications de versions</span>
        <div className="ml-auto">
          <svg 
            xmlns="http://www.w3.org/2000/svg" 
            width="18" 
            height="18" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2" 
            strokeLinecap="round" 
            strokeLinejoin="round"
            className={`transition-transform duration-300 ${expandedHistory ? 'transform rotate-180' : ''}`}
          >
            <polyline points="6 9 12 15 18 9"></polyline>
          </svg>
        </div>
      </div>
      
      <div 
        className={`version-history-content transition-all duration-300 overflow-hidden ${
          expandedHistory ? 'max-h-full opacity-100 p-4' : 'max-h-0 opacity-0 p-0'
        }`}
      >
        {versionHistory.map((serverHistory, serverIndex) => (
          <div key={serverIndex} className="mb-4 border rounded-lg overflow-hidden">
            <div 
              className="server-card-header p-3 flex items-center justify-between cursor-pointer hover:bg-gray-50"
              style={{ borderLeft: `4px solid ${serverHistory.color || '#3B82F6'}` }}
              onClick={() => toggleServerHistory(serverIndex)}
            >
              <div className="server-info flex items-center">
                <div className="server-icon mr-2">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="8" rx="2" ry="2"></rect>
                    <rect x="2" y="14" width="20" height="8" rx="2" ry="2"></rect>
                    <line x1="6" y1="6" x2="6" y2="6"></line>
                    <line x1="6" y1="18" x2="6" y2="18"></line>
                  </svg>
                </div>
                <div className="server-name font-medium" style={{ color: serverHistory.color }}>
                  {serverHistory.serverName || `Serveur ${serverIndex + 1}`}
                </div>
                <div 
                  className="server-badge ml-2 px-2 py-0.5 text-xs rounded text-white"
                  style={{ background: serverHistory.color ? `linear-gradient(135deg, ${serverHistory.color}, ${lightenColor(serverHistory.color, 40)})` : 'linear-gradient(135deg, #3B82F6, #60A5FA)' }}
                >
                  {serverHistory.serverType}
                </div>
              </div>
              <svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="16" 
                height="16" 
                viewBox="0 0 24 24" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="2" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                className={`transition-transform duration-300 ${expandedServer === serverIndex ? 'transform rotate-180' : ''}`}
              >
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            </div>
            
            {expandedServer === serverIndex && (
              <div className="p-3 bg-gray-50">
                {/* Liste des composants avec possibilité de les développer individuellement */}
                <div className="mb-3">
                  <h5 className="text-sm font-medium text-gray-500 mb-2">COMPOSANTS</h5>
                  <div className="flex flex-wrap gap-2">
                    {groupVersionsByComponent(serverHistory).map((component, compIndex) => (
                      <button
                        key={compIndex}
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleComponent(serverIndex, component.name);
                        }}
                        className={`flex items-center px-3 py-1 rounded text-sm ${
                          isComponentExpanded(serverIndex, component.name) 
                            ? 'bg-blue-600 text-white' 
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        {component.name}
                        <span className="ml-1 bg-white text-blue-600 text-xs px-1.5 py-0.5 rounded-full">
                          {component.versions.length}
                        </span>
                        <svg 
                          xmlns="http://www.w3.org/2000/svg" 
                          width="14" 
                          height="14" 
                          viewBox="0 0 24 24" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          strokeLinecap="round" 
                          strokeLinejoin="round"
                          className={`ml-1 transition-transform duration-300 ${isComponentExpanded(serverIndex, component.name) ? 'transform rotate-180' : ''}`}
                        >
                          <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Affichage des versions par composant */}
                {groupVersionsByComponent(serverHistory).map((component, compIndex) => (
                  isComponentExpanded(serverIndex, component.name) && (
                    <div key={compIndex} className="mb-4 bg-white rounded border p-3">
                      <h6 className="font-medium mb-2">{component.name} - Historique des versions</h6>
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                            <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Version</th>
                            <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {component.versions.slice().sort((a, b) => new Date(b.date) - new Date(a.date)).map((version, vIndex) => (
                            <tr key={vIndex} className={vIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                              <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-500">{formatDate(version.date)}</td>
                              <td className="px-2 py-2 whitespace-nowrap text-xs font-mono text-gray-900">{version.version}</td>
                              <td className="px-2 py-2 whitespace-nowrap">
                                <span className={getBadgeClass(version.updateType)}>
                                  {version.updateType}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                ))}
                
                {/* Affichage de tous les composants (vue globale) */}
                <button 
                  className="text-sm text-blue-600 hover:underline mb-2 flex items-center"
                  onClick={(e) => {
                    e.stopPropagation();
                    // Réinitialiser tous les composants expandés pour ce serveur
                    const newExpandedComps = {...expandedComponents};
                    groupVersionsByComponent(serverHistory).forEach((comp) => {
                      const key = `${serverIndex}-${comp.name}`;
                      newExpandedComps[key] = false;
                    });
                    setExpandedComponents(newExpandedComps);
                  }}
                >
                  <svg 
                    xmlns="http://www.w3.org/2000/svg" 
                    width="14" 
                    height="14" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                    className="mr-1"
                  >
                    <line x1="19" y1="12" x2="5" y2="12"></line>
                    <polyline points="12 19 5 12 12 5"></polyline>
                  </svg>
                  Voir toutes les versions
                </button>
                
                <div className="bg-white rounded border overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Composant</th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Version</th>
                        <th scope="col" className="px-2 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {flattenServerVersionHistory(serverHistory).map((item, itemIndex) => (
                        <tr key={itemIndex} className={itemIndex % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                          <td className="px-2 py-2 whitespace-nowrap text-xs text-gray-500">{formatDate(item.date)}</td>
                          <td className="px-2 py-2 whitespace-nowrap text-xs font-medium text-gray-900">{item.component}</td>
                          <td className="px-2 py-2 whitespace-nowrap text-xs font-mono text-gray-900">{item.version}</td>
                          <td className="px-2 py-2 whitespace-nowrap">
                            <span className={getBadgeClass(item.updateType)}>
                              {item.updateType}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default VersionHistoryCompact;